import type {FarmState,FarmEvent,Cerda,Verraco,Monta,Celo,Parto,Camada,Lote,TareaManual,PrintRecord} from './model';
import {addDays,daysBetween,todayISO,fmtDateWritten} from './dates';

export function createDomain(state:FarmState) {
function montasDeCerda(arete: string){ return state.montas.filter(m=>m.arete===arete).sort((a,b)=>a.fecha<b.fecha?1:-1); }
function celosDeCerda(arete: string){ return state.celos.filter(c=>c.arete===arete).sort((a,b)=>a.fecha<b.fecha?1:-1); }
function repeticionesDeCerda(arete: string){ return state.repeticiones.filter(r=>r.arete===arete).sort((a,b)=>a.fecha<b.fecha?1:-1); }
function partosDeCerda(arete: string){ return state.partos.filter(p=>p.arete===arete).sort((a,b)=>a.fecha<b.fecha?1:-1); }

const CERDA_ESTADOS_INACTIVOS=['muerta','vendida','retirada'];
function estadoCerdaClase(estado: string){
  return {preñada:'pre',vacía:'vacia',montada:'montada',repetida:'repetida',lactando:'lactando',reemplazo:'vacia'}[estado]||estado;
}
function getCerdaEstadoReproductivo(arete: string){
  const eventos: {fecha:string;orden:number;estado:string}[]=[];
  montasDeCerda(arete).forEach(monta=>{
    eventos.push({fecha:monta.fecha,orden:1,estado:'montada'});
    if(monta.gestacionConfirmada) eventos.push({fecha:monta.fechaConfirmacion||monta.fecha,orden:2,estado:'preñada'});
  });
  repeticionesDeCerda(arete).forEach(repeticion=>eventos.push({fecha:repeticion.fecha,orden:3,estado:'repetida'}));
  partosDeCerda(arete).forEach(parto=>eventos.push({fecha:parto.fecha,orden:4,estado:'lactando'}));
  celosDeCerda(arete).forEach(celo=>{
    const ultimaMonta=montasDeCerda(arete).find(monta=>monta.fecha<=celo.fecha);
    const ultimaRepeticion=repeticionesDeCerda(arete).find(repeticion=>repeticion.fecha<=celo.fecha);
    if(!ultimaMonta||(ultimaRepeticion && ultimaRepeticion.fecha>=ultimaMonta.fecha)||partosDeCerda(arete).some(parto=>parto.fecha>=ultimaMonta.fecha&&parto.fecha<=celo.fecha)){
      eventos.push({fecha:celo.fecha,orden:5,estado:'vacía'});
    }
  });
  eventos.sort((a,b)=>a.fecha===b.fecha?a.orden-b.orden:a.fecha<b.fecha?-1:1);
  return eventos[eventos.length-1]?.estado||(getCerda(arete)?.estado==='reemplazo'?'reemplazo':'vacía');
}

function avisoEstadoCerda(cerda: Cerda){
  if(cerda.estado==='montada'){
    const monta=getActiveMonta(cerda.arete);
    if(monta&&daysBetween(monta.fecha,todayISO())>=21) return 'Revisar gestación o registrar repetición';
  }
  if(cerda.estado==='lactando'){
    const parto=getActiveParto(cerda.arete);
    if(parto&&daysBetween(parto.fecha,todayISO())>21) return 'Revisar destete y actualizar estado';
  }
  return '';
}

function avisoRepeticiones(arete: string): string | null {
  const total=state.repeticiones.filter(repeticion=>repeticion.arete===arete).length;
  return total>2?`Esta cerda acumula ${total} repeticiones registradas en total. Revisa su historial reproductivo.`:null;
}

function getActiveMonta(arete: string){
  const montas=montasDeCerda(arete);
  if(!montas.length) return null;
  const ultima=montas[0];
  const reps=repeticionesDeCerda(arete);
  if(reps.length && reps[0].fecha>=ultima.fecha) return null; // repitió después de la última monta o el mismo día
  const partos=partosDeCerda(arete);
  if(partos.length && partos[0].fecha>=ultima.fecha) return null; // ya parió
  return ultima;
}

function getActiveParto(arete: string){
  const partos=partosDeCerda(arete);
  if(!partos.length) return null;
  const ultimo=partos[0];
  const ultimaMonta=montasDeCerda(arete)[0];
  if(ultimaMonta&&ultimaMonta.fecha>ultimo.fecha) return null;
  const ultimaRepeticion=repeticionesDeCerda(arete)[0];
  if(ultimaRepeticion&&ultimaRepeticion.fecha>=ultimo.fecha) return null;
  const ultimoCelo=celosDeCerda(arete)[0];
  if(ultimoCelo&&ultimoCelo.fecha>=ultimo.fecha) return null;
  return ultimo;
}

function fechasPosibleCelo(celo: Celo){
  return [18,19,20,21].map(dia=>({dia,fecha:addDays(celo.fecha,dia-1)}));
}

function cerdaEnObservacion(cerda: Cerda){
  if(typeof cerda.enObservacion==='boolean') return cerda.enObservacion;
  return state.partos.some(p=>p.arete===cerda.arete&&(p.intervencionManual===true||(+p.nacidosMuertos||0)>0))||
    state.repeticiones.some(r=>r.arete===cerda.arete&&r.infeccion)||
    state.mortalidad.some(m=>state.camadas.some(c=>c.id===m.camadaId&&c.arete===cerda.arete));
}

function ponerCerdaEnObservacion(arete: string, motivo: string){
  const cerda=getCerda(arete);
  if(!cerda) return;
  cerda.enObservacion=true;
  if(motivo&&!cerda.motivoObservacion) cerda.motivoObservacion=motivo;
}

function marcarParaImprimir(registro: PrintRecord | Cerda | Verraco | Lote | undefined | null, motivo: string){
  if(!registro) return;
  registro.imprimirPendiente=true;
  registro.motivoImpresion=motivo;
}

function fechaFinLactancia(parto: Parto){
  const siguiente=[...state.celos,...state.montas,...state.repeticiones,...state.partos.filter(p=>p.id!==parto.id)]
    .filter(registro=>registro.arete===parto.arete&&registro.fecha>parto.fecha)
    .map(registro=>registro.fecha).sort()[0];
  return siguiente?addDays(siguiente,-1):null;
}

function tramosRacionLactancia(parto: Parto){
  const raciones=state.racionesLactancia.filter(r=>r.partoId===parto.id).slice().sort((a,b)=>a.fechaDesde.localeCompare(b.fechaDesde));
  const finPeriodo=fechaFinLactancia(parto);
  return raciones.map((racion,index)=>{
    const siguiente=raciones[index+1]?.fechaDesde;
    const fin=[todayISO(),finPeriodo,siguiente?addDays(siguiente,-1):null].filter((d): d is string=>Boolean(d)).sort()[0];
    const dias=fin>=racion.fechaDesde?daysBetween(racion.fechaDesde,fin)+1:0;
    return {racion,hasta:fin,dias,consumoCerda:dias*(+racion.librasCerda||0),consumoLechones:dias*(+racion.librasLechones||0)};
  });
}

function getActiveCelo(arete: string){
  const ultimo=celosDeCerda(arete)[0];
  if(!ultimo) return null;
  // Una monta, repetición o parto posterior inicia otra etapa; conserva el celo en el historial.
  const ultimoEvento=[montasDeCerda(arete)[0]?.fecha,repeticionesDeCerda(arete)[0]?.fecha,partosDeCerda(arete)[0]?.fecha]
    .filter(Boolean).sort().pop();
  if(ultimoEvento&&ultimoEvento>=ultimo.fecha) return null;
  const montaConfirmada=montasDeCerda(arete).find(m=>m.gestacionConfirmada&&(m.fechaConfirmacion||'')>=ultimo.fecha);
  return montaConfirmada?null:ultimo;
}

function getCamada(id?: string | null){ return state.camadas.find(c=>c.id===id); }
function getLote(id?: string | null){ return state.lotes.find(l=>l.id===id); }
function getCerda(arete?: string | null){ return state.cerdas.find(c=>c.arete===arete); }
function getVerraco(id?: string | null){ return state.verracos.find(v=>v.id===id); }
function getEspacio(id?: string | null){ return state.espacios.find(e=>e.id===id); }
function getEspacioCerda(id?: string | null){ return state.espaciosCerdas.find(e=>e.id===id); }
function normalizarOrdenEspaciosCerdas(){
  ['gestacion','paridera'].forEach(tipo=>{
    state.espaciosCerdas.filter(e=>e.tipo===tipo).sort((a,b)=>(+a.orden||0)-(+b.orden||0)||a.nombre.localeCompare(b.nombre,undefined,{numeric:true})).forEach((e,index)=>{ e.orden=index+1; });
  });
}
function verracoIdsDeMonta(monta?: Monta | null){
  if(!monta) return [];
  const ids=Array.isArray(monta.verracoIds)?monta.verracoIds.filter(Boolean):[];
  if(monta.verracoId&&!ids.includes(monta.verracoId)) ids.unshift(monta.verracoId);
  return [...new Set(ids)].slice(0,2);
}
function montaIncluyeVerraco(monta: Monta, verracoId: string){ return verracoIdsDeMonta(monta).includes(verracoId); }
function nombresVerracosDeMonta(monta?: Monta | null){
  const propios=verracoIdsDeMonta(monta).map(id=>getVerraco(id)?.nombre||'Verraco eliminado');
  if(propios.length) return propios.join(' + ');
  if(monta?.verracoExterno) return `${monta.verracoExterno} (externo)`;
  if(monta?.proveedor) return `Semen de ${monta.proveedor}`;
  return monta?.tipoServicio==='ia'?'Semen externo / N/A':'Externo / N/A';
}
function getMontaDeParto(parto?: Parto | null){
  if(!parto) return null;
  // El padre pertenece al ciclo de ese parto, no necesariamente a la última monta actual.
  const partoAnterior=partosDeCerda(parto.arete).find(registro=>registro.id!==parto.id&&registro.fecha<parto.fecha);
  const ultimaRepeticion=repeticionesDeCerda(parto.arete).find(registro=>registro.fecha<=parto.fecha);
  return montasDeCerda(parto.arete).find(monta=>monta.fecha<=parto.fecha&&
    (!partoAnterior||monta.fecha>partoAnterior.fecha)&&
    (!ultimaRepeticion||monta.fecha>ultimaRepeticion.fecha))||null;
}
function nombrePadreDeParto(parto?: Parto | null){
  const monta=getMontaDeParto(parto);
  if(!monta) return 'No registrado';
  return nombresVerracosDeMonta(monta);
}
function origenDeCamada(camada?: Camada | null){
  const parto=state.partos.find(p=>p.id===camada?.partoId);
  return {parto,madre:parto?.arete||camada?.arete||'No registrada',padre:nombrePadreDeParto(parto)};
}
function eventosProtocoloCerdaActual(arete: string){
  const monta=getActiveMonta(arete);
  const ultimoParto=getActiveParto(arete);
  const celo=getActiveCelo(arete);
  return generateAllEvents().filter(e=>e.ref&&e.ref.type==='cerda'&&e.ref.id===arete).filter(e=>
    (monta&&(e.doneKey.startsWith(`repro|${arete}|${monta.id}|`)||e.doneKey.startsWith(`repwin|${arete}|${monta.id}|`)))||
    (ultimoParto&&e.doneKey.startsWith(`posparto|${ultimoParto.id}|`))||
    (celo&&e.doneKey.startsWith(`celo|${celo.id}|`))||
    (getCerda(arete)?.estado==='reemplazo'&&e.doneKey.startsWith(`reemplazoCerda|${arete}|`))
  ).sort((a,b)=>a.date===b.date?a.prioridad-b.prioridad:a.date<b.date?-1:1);
}
function aplicarFechaPersonalizada(evento: FarmEvent){
  const cambio=state.eventOverrides?.[evento.doneKey];
  if(cambio){
    if(Object.prototype.hasOwnProperty.call(cambio,'date')){
      if(!evento.fechaOriginal) evento.fechaOriginal=evento.date;
      evento.date=cambio.date ?? '';
    }
    if(Object.prototype.hasOwnProperty.call(cambio,'title')) evento.action=cambio.title;
    if(Object.prototype.hasOwnProperty.call(cambio,'detail')) evento.detail=cambio.detail;
  }
  return evento;
}
function fechaEventoVisible(evento: FarmEvent){ return evento.date?fmtDateWritten(evento.date):'Sin fecha'; }
function eventosProtocoloReemplazo(tipo: string, animal: Cerda | Verraco){
  const seccion=tipo==='cerda'?'reemplazoCerda':'reemplazoVerraco';
  const referencia=animal.fechaInicioReemplazo||animal.fechaEntrada;
  if(animal.estado!=='reemplazo'||!referencia) return [];
  const id='arete' in animal?animal.arete:animal.id;
  return state.protocolos[seccion].filter(p=>p.activo!==false).map(p=>({
    date:addDays(referencia,p.dia-1),tipo:'protocolo',prioridad:2,label:`${id} — ${p.nombre}`,
    action:p.nombre,detail:p.detalle||'',protocolDay:p.dia,protocolGroup:tipo==='cerda'?'Reemplazo de cerda':'Reemplazo de verraco',
    referenceLabel:'desde el inicio de reemplazo',subject:`${tipo==='cerda'?'Cerda':'Verraco'} ${'nombre' in animal?animal.nombre:id}`,
    doneKey:`${seccion}|${id}|${p.id}`,ref:{type:tipo,id}
  })).map(aplicarFechaPersonalizada);
}
function eventosProtocoloLechonesCerda(arete: string, soloCamadaId: string | null = null){
  const camadas=state.camadas.filter(camada=>camada.arete===arete&&(!soloCamadaId||camada.id===soloCamadaId));
  return camadas.flatMap(camada=>state.protocolos.lechones.filter(p=>p.activo!==false).map(p=>({
    date:addDays(camada.fechaNacimiento,p.dia),tipo:'protocolo',prioridad:2,label:`${camada.id} — ${p.nombre}`,
    action:p.nombre,detail:p.detalle||'',protocolDay:p.dia,protocolGroup:'Lechones',referenceLabel:'desde el nacimiento',
    subject:`Camada ${camada.id}`,camadaId:camada.id,doneKey:`lechon|${camada.id}|${p.id}`,ref:{type:'camada',id:camada.id}
  }))).map(aplicarFechaPersonalizada).sort((a,b)=>a.date===b.date?(a.protocolDay??0)-(b.protocolDay??0):a.date<b.date?-1:1);
}
function fechaNacimientoReferenciaLote(lote?: Lote | null){
  return lote?.fechaNacimientoReferencia||addDays(lote?.fechaCreacion,-(lote?.diaEdadAlCrear??1));
}
function eventosProtocoloLote(lote: Lote){
  const referencia=fechaNacimientoReferenciaLote(lote);
  return state.protocolos.lote.filter(p=>p.activo!==false).map(p=>({
    date:addDays(referencia,p.dia),tipo:'protocolo',prioridad:2,label:`${lote.id} — ${p.nombre}`,
    action:p.nombre,detail:p.detalle||'',protocolDay:p.dia,protocolGroup:'Lote',referenceLabel:'desde el nacimiento estimado',
    subject:`Lote ${lote.id}`,doneKey:`lote|${lote.id}|${p.id}`,ref:{type:'lote',id:lote.id}
  })).map(aplicarFechaPersonalizada).sort((a,b)=>a.date===b.date?a.prioridad-b.prioridad:a.date<b.date?-1:1);
}

function destinatariosTarea(tipo: string){
  if(tipo==='cerda') return state.cerdas.map(c=>({id:c.arete,nombre:`Cerda ${c.arete}`})).sort((a,b)=>a.id.localeCompare(b.id,undefined,{numeric:true}));
  if(tipo==='verraco') return state.verracos.map(v=>({id:v.id,nombre:`Verraco ${v.nombre}`})).sort((a,b)=>a.nombre.localeCompare(b.nombre));
  if(tipo==='lote') return state.lotes.map(l=>({id:l.id,nombre:`Lote ${l.id}`})).sort((a,b)=>a.id.localeCompare(b.id,undefined,{numeric:true}));
  return [];
}
function nombreDestinatarioTarea(tarea: TareaManual){
  if(!tarea.destinatarioTipo||tarea.destinatarioTipo==='general') return 'Tarea general';
  return destinatariosTarea(tarea.destinatarioTipo||'general').find(d=>d.id===tarea.destinatarioId)?.nombre||tarea.destinatarioNombre||'Destinatario no disponible';
}

function generateAllEvents(): FarmEvent[]{
  const events: FarmEvent[]=[];
  state.cerdas.forEach(cerda=>{
    if(!CERDA_ESTADOS_INACTIVOS.includes(cerda.estado)){
      events.push(...eventosProtocoloReemplazo('cerda',cerda));
      const monta=getActiveMonta(cerda.arete);
      if(monta){
        state.protocolos.reproduccion.filter(p=>p.activo!==false).forEach(p=>{
          if(p.aplicaA==='todas' || (p.aplicaA==='primerizas' && cerda.primeriza)){
            const date=addDays(monta.fecha,p.dia-1);
            const tipo = p.dia===114?'parto':'protocolo';
            events.push({date,tipo,prioridad:tipo==='parto'?1:2,label:`${cerda.arete} — ${p.nombre}`,action:p.nombre,detail:p.detalle||'',protocolDay:p.dia,protocolGroup:'Reproducción',referenceLabel:'desde la última monta',subject:`Cerda ${cerda.arete}`,doneKey:`repro|${cerda.arete}|${monta.id}|${p.id}`,ref:{type:'cerda',id:cerda.arete}});
          }
        });
        const wStart=addDays(monta.fecha,17), wEnd=addDays(monta.fecha,20);
        for(let d=wStart; d<=wEnd; d=addDays(d,1)){
          events.push({date:d,tipo:'repeticion',prioridad:3,label:`${cerda.arete} — Posible repetición (ventana)`,action:'Posible repetición (ventana)',detail:'Revisar signos de repetición',protocolDay:daysBetween(monta.fecha,d)+1,protocolGroup:'Reproducción',referenceLabel:'desde la última monta',subject:`Cerda ${cerda.arete}`,doneKey:`repwin|${cerda.arete}|${monta.id}|${d}`,ref:{type:'cerda',id:cerda.arete}});
        }
      }
    }
  });
  state.celos.forEach(celo=>{
    const cerda=getCerda(celo.arete);
    if(!cerda||CERDA_ESTADOS_INACTIVOS.includes(cerda.estado)||getActiveCelo(celo.arete)?.id!==celo.id) return;
    for(let dia=18;dia<=21;dia++){
      const date=addDays(celo.fecha,dia-1);
      events.push({date,tipo:'celo',prioridad:3,label:`${celo.arete} — Posible presencia de celo`,action:'Posible presencia de celo',
        detail:celo.observaciones||'Revisar signos de celo',protocolDay:dia,protocolGroup:'Celo',referenceLabel:'desde el celo registrado',
        subject:`Cerda ${celo.arete}`,doneKey:`celo|${celo.id}|${dia}`,ref:{type:'cerda',id:celo.arete}});
    }
  });
  state.partos.forEach(parto=>{
    if(getActiveParto(parto.arete)?.id!==parto.id) return;
    state.protocolos.posparto.filter(p=>p.activo!==false).forEach(p=>{
      const date=addDays(parto.fecha,p.dia);
      events.push({date,tipo:'protocolo',prioridad:2,label:`${parto.arete} (posparto) — ${p.nombre}`,action:p.nombre,detail:p.detalle||'',protocolDay:p.dia,protocolGroup:'Maternidad',referenceLabel:'desde el parto',subject:`Cerda ${parto.arete}`,doneKey:`posparto|${parto.id}|${p.id}`,ref:{type:'cerda',id:parto.arete}});
    });
  });
  state.camadas.forEach(camada=>{
    if(camada.loteId) return; // ya pasó a lote, ya no aplica protocolo de lechón individual
    events.push({date:addDays(camada.fechaNacimiento,21),tipo:'destete',prioridad:1,label:`${camada.id} — Destete (día 21)`,action:'Destete (día 21)',detail:'Revisar traslado de la camada al lote',protocolDay:21,protocolGroup:'Lechones',referenceLabel:'desde el nacimiento',subject:`Camada ${camada.id}`,doneKey:`destete|${camada.id}`,ref:{type:'camada',id:camada.id}});
    state.protocolos.lechones.filter(p=>p.activo!==false).forEach(p=>{
      const date=addDays(camada.fechaNacimiento,p.dia);
      events.push({date,tipo:'protocolo',prioridad:2,label:`${camada.id} — ${p.nombre}`,action:p.nombre,detail:p.detalle||'',protocolDay:p.dia,protocolGroup:'Lechones',referenceLabel:'desde el nacimiento',subject:`Camada ${camada.id}`,doneKey:`lechon|${camada.id}|${p.id}`,ref:{type:'camada',id:camada.id}});
    });
  });
  state.lotes.forEach(lote=>{
    if(lote.estado==='finalizado') return;
    events.push(...eventosProtocoloLote(lote));
  });
  state.verracos.forEach(verraco=>events.push(...eventosProtocoloReemplazo('verraco',verraco)));
  state.tareasManual.forEach(tarea=>{
    events.push({
      date:tarea.fecha, tipo:'manual', prioridad:2, label:`Tarea — ${tarea.titulo}`,
      action:tarea.titulo, detail:tarea.detalle||'', protocolGroup:tarea.categoria||'Tarea general',
      subject:nombreDestinatarioTarea(tarea), doneKey:`manual|${tarea.id}`, manualId:tarea.id,
      ref:{type:'tarea',id:tarea.id}
    });
  });
  return events.map(aplicarFechaPersonalizada);
}

function isDone(key: string){ return state.tareasHechas.includes(key); }
function eventTypeLabel(e: FarmEvent){
  if(e.tipo==='parto') return 'PARTO';
  if(e.tipo==='destete') return 'DESTETE';
  if(e.tipo==='repeticion') return 'REPETICIÓN';
  if(e.tipo==='celo') return 'CELO';
  if(e.tipo==='manual') return 'TAREA';
  return 'PROTOCOLO';
}
function eventAction(e: FarmEvent){ return e.action || e.label.split(' — ')[1] || e.label; }
function eventSubject(e: FarmEvent){ return e.subject || (e.ref?.id ? `${e.ref.type==='cerda'?'Cerda':e.ref.type==='camada'?'Camada':'Lote'} ${e.ref.id}` : ''); }
const TASK_AREAS=[
  {id:'maternidad',label:'Maternidad'},
  {id:'destete',label:'Destete'},
  {id:'ceba',label:'Ceba'},
  {id:'gestacion',label:'Gestación'},
  {id:'verracos',label:'Verracos'},
  {id:'reemplazo',label:'Reemplazo'},
  {id:'general',label:'Sin área asignada'}
];
function eventArea(e: FarmEvent){
  if(e.tipo==='destete') return 'destete'; // La tarea del día 21 inicia el traslado al destete.
  if(e.protocolGroup?.startsWith('Reemplazo de ')) return 'reemplazo';
  const tarea=e.manualId?state.tareasManual.find(t=>t.id===e.manualId):null;
  const tipo=tarea?tarea.destinatarioTipo:e.ref?.type;
  const id=tarea?tarea.destinatarioId:e.ref?.id;
  if(tipo==='camada') return 'maternidad';
  if(tipo==='cerda'){
    const cerda=getCerda(id);
    if(!cerda) return 'general';
    if(cerda.estado==='reemplazo') return 'reemplazo';
    const ubicacion=state.espaciosCerdas.find(espacio=>espacio.cerdaArete===id);
    if(ubicacion?.tipo==='paridera') return 'maternidad';
    if(ubicacion?.tipo==='gestacion') return 'gestacion';
    return e.protocolGroup==='Maternidad'||cerda.estado==='lactando'?'maternidad':'gestacion';
  }
  if(tipo==='verraco') return getVerraco(id)?.estado==='reemplazo'?'reemplazo':'verracos';
  if(tipo==='lote'){
    const lote=getLote(id);
    if(!lote) return 'general';
    const historial=[...(lote.historial||[])].reverse();
    const etapaEnFecha=e.date?historial.find(item=>item.fechaEntrada<=e.date&&(!item.fechaSalida||e.date<item.fechaSalida))?.tipo:null;
    const etapa=etapaEnFecha||getEspacio(lote.espacioActual)?.tipo||historial.find(item=>!item.fechaSalida)?.tipo;
    return etapa==='paridera'?'maternidad':etapa==='ceba'?'ceba':etapa==='destete'?'destete':'general';
  }
  return 'general';
}
function eventAreaLabel(e: FarmEvent){ return TASK_AREAS.find(area=>area.id===eventArea(e))?.label||'Sin área asignada'; }
function syncCerdaReproState(arete: string){
  const cerda=getCerda(arete);
  if(!cerda) return;
  cerda.numPartos=(cerda.partosPrevios||0)+partosDeCerda(arete).length;
  if(cerda.numPartos>0) cerda.primeriza=false;
  if(!CERDA_ESTADOS_INACTIVOS.includes(cerda.estado)) cerda.estado=getCerdaEstadoReproductivo(arete);
}

function getCamadaDeParto(partoId: string){ return state.camadas.find(c=>c.partoId===partoId); }
return {montasDeCerda,celosDeCerda,repeticionesDeCerda,partosDeCerda,estadoCerdaClase,getCerdaEstadoReproductivo,avisoEstadoCerda,avisoRepeticiones,getActiveMonta,getActiveParto,fechasPosibleCelo,cerdaEnObservacion,ponerCerdaEnObservacion,marcarParaImprimir,fechaFinLactancia,tramosRacionLactancia,getActiveCelo,getCamada,getLote,getCerda,getVerraco,getEspacio,getEspacioCerda,normalizarOrdenEspaciosCerdas,verracoIdsDeMonta,montaIncluyeVerraco,nombresVerracosDeMonta,getMontaDeParto,nombrePadreDeParto,origenDeCamada,eventosProtocoloCerdaActual,aplicarFechaPersonalizada,fechaEventoVisible,eventosProtocoloReemplazo,eventosProtocoloLechonesCerda,fechaNacimientoReferenciaLote,eventosProtocoloLote,destinatariosTarea,nombreDestinatarioTarea,generateAllEvents,isDone,eventTypeLabel,eventAction,eventSubject,eventArea,eventAreaLabel,syncCerdaReproState,getCamadaDeParto,CERDA_ESTADOS_INACTIVOS,TASK_AREAS};
}
export type FarmDomain = ReturnType<typeof createDomain>;
